import { Button } from '@aqarmap/aqarmap-ui';
import { useForm } from 'react-hook-form';
import Section from '~addListing/pages/InitializeStep/Section';
import { zodResolver } from '@hookform/resolvers/zod';
import { initializeListingSchema } from '~addListing/utils/schema';
import PropertyType from '~addListing/pages/InitializeStep/PropertyType';
import ReferenceNumber from '~addListing/pages/InitializeStep/ReferenceNumber';
import { useTranslation } from 'react-i18next';
import {
  useCloneListingMutation,
  useGetListingChildrenMutation,
  useGetUserQuery,
  useInitializeListingMutation,
} from '~/redux/api/userApiSlice';
import ListingMap from '~addListing/pages/InitializeStep/ListingMap';
import {
  AddListingInitializeType,
  CloneListingUnitType,
  LocationType,
} from '~addListing/types';
import { useNavigate } from 'react-router-dom';
import { UserTypesEnum } from '~/constants/enums';
import Location from '~addListing/pages/InitializeStep/Location/index';
import { saveListingData } from '~addListing/utils/LocalStorage';
import { useFullRoutes } from '~/hooks';
import { useState } from 'react';
import { useAppDispatch } from '~/redux/hooks';
import { closeModal, openModal } from '~/redux/features/uiState/uiStateSlice';
import MarketListingTypeModal from '~addListing/pages/InitializeStep/MarketListingTypeModal';
import CloneUnitsModal from '~addListing/pages/InitializeStep/CloneUnitsModal';
import { SectionEnum } from '~/constants/enums/listingEnums';
import { SectionType } from '~addListing/types';

const InitializeAddListing = () => {
  const { t } = useTranslation('app');
  const { data } = useGetUserQuery();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { ADD_LISTING_URL } = useFullRoutes();
  const [selectedSection, setSelectedSection] = useState<SectionType>();
  const [selectedLocation, setSelectedLocation] = useState<LocationType | null>(
    null,
  );
  const [pendingListingData, setPendingListingData] = useState<{
    sectionId: number;
    propertyId: number;
    formData: AddListingInitializeType;
  } | null>(null);
  const [units, setUnits] = useState<CloneListingUnitType[]>([]);
  const [selectedUnitId, setSelectedUnitId] = useState<number | null>(null);
  const userIsVerified =
    data?.user?.is_phone_verified || data?.user?.has_subscription_plan;
  const {
    setValue,
    handleSubmit,
    formState: { errors },
    clearErrors,
    watch,
  } = useForm<AddListingInitializeType>({
    resolver: zodResolver(initializeListingSchema),
  });
  const [initializeListing, { isLoading }] = useInitializeListingMutation();

  const [getListingChildren, { isLoading: isListingChildrenLoading }] =
    useGetListingChildrenMutation();

  const [cloneListing, { isLoading: isCloneLoading }] =
    useCloneListingMutation();

  const showReferenceNumber =
    data?.user.user_type === UserTypesEnum.BROKER ||
    data?.user.user_type === UserTypesEnum.COMPOUND_DEVELOPER ||
    data?.user.user_type === UserTypesEnum.SMALL_DEVELOPER;

  const handleNavigateToDetails = (listingId: number) => {
    navigate(ADD_LISTING_URL.DETAILS(listingId));
  };

  const getChildrenUnits = (
    responseData: CloneListingUnitType[] | { data: CloneListingUnitType[] },
  ) => {
    if (Array.isArray(responseData)) return responseData;
    return responseData.data;
  };

  const handleInitializeListing = async (data: AddListingInitializeType) => {
    const res = await initializeListing(data);

    if ('data' in res) {
      const listingId = res.data.listing.id;
      if (showReferenceNumber)
        saveListingData(`${listingId}`, {
          referenceNumber: data.referenceNumber,
        });
      return listingId;
    }

    return null;
  };

  const onSubmit = async (formData: AddListingInitializeType) => {
    const data = { ...formData };
    if (!showReferenceNumber) delete data.referenceNumber;

    if (
      selectedLocation?.listing &&
      selectedLocation?.is_compound_location &&
      selectedSection?.slug === SectionEnum.FORSALE
    ) {
      setPendingListingData({
        sectionId: data.section,
        propertyId: data.propertyType,
        formData: data,
      });
      dispatch(openModal('marketListingTypeModal'));
      return;
    }

    const listingId = await handleInitializeListing(data);
    if (listingId) {
      handleNavigateToDetails(listingId);
    } else {
      alert('error');
    }
  };

  const handleSelectResale = async () => {
    if (!pendingListingData) return;

    const listingId = await handleInitializeListing(
      pendingListingData.formData,
    );
    if (!listingId) {
      alert('error');
      return;
    }

    dispatch(closeModal('marketListingTypeModal'));
    handleNavigateToDetails(listingId);
  };

  const handleSelectPrimary = async () => {
    if (!selectedLocation?.listing || !pendingListingData) return;
    const res = await getListingChildren({
      listingId: selectedLocation?.listing?.id,
      propertyType: pendingListingData?.propertyId,
    });

    if ('data' in res) {
      setUnits(getChildrenUnits(res.data));
      setSelectedUnitId(null);
      dispatch(closeModal('marketListingTypeModal'));
      dispatch(openModal('cloneUnitsModal'));
      return;
    }

    alert('error');
  };

  const handleSkipClone = async () => {
    if (!pendingListingData) return;

    const listingId = await handleInitializeListing(
      pendingListingData.formData,
    );
    if (!listingId) {
      alert('error');
      return;
    }

    dispatch(closeModal('cloneUnitsModal'));
    handleNavigateToDetails(listingId);
  };

  const handleCloneUnit = async () => {
    if (!pendingListingData || !selectedLocation?.listing?.id) return;

    const unitId = selectedUnitId || selectedLocation.listing.id;

    const res = await cloneListing({
      unitId,
      sectionId: pendingListingData.sectionId,
      propertyId: pendingListingData.propertyId,
    });

    if ('data' in res) {
      const clonedListingId = res.data;
      dispatch(closeModal('cloneUnitsModal'));
      handleNavigateToDetails(clonedListingId);
      return;
    }
  };

  return (
    <>
      <div className="flex flex-col gap-4x w-full">
        <p className="text-title_2">{t('basicInfo')}</p>
        <Section
          selectedSection={selectedSection}
          setSelectedSection={setSelectedSection}
          setValue={setValue}
        />
        <PropertyType
          setValue={setValue}
          errors={errors}
          clearErrors={clearErrors}
        />
        <Location
          setValue={(field, value) =>
            setValue(field as keyof AddListingInitializeType, value)
          }
          errors={errors}
          clearErrors={clearErrors}
          onSelectLocation={setSelectedLocation}
        />
        <ListingMap
          setValue={setValue}
          locationPosition={{
            lat: watch('centerLat'),
            lng: watch('centerLng'),
          }}
        />
        {showReferenceNumber && <ReferenceNumber setValue={setValue} />}
        <div>
          <Button
            onClick={handleSubmit(onSubmit)}
            variant="primary"
            size="medium"
            disabled={!userIsVerified}
            className="w-[135px]"
            isLoading={isLoading}
          >
            {t('follow')}
          </Button>
        </div>
      </div>
      <MarketListingTypeModal
        onSelectPrimary={handleSelectPrimary}
        onSelectResale={handleSelectResale}
        isLoading={isListingChildrenLoading}
      />
      <CloneUnitsModal
        title={selectedLocation?.title || t('cloneUnitsModalTitle')}
        units={units}
        selectedUnitId={selectedUnitId}
        onSelectUnit={setSelectedUnitId}
        onSkip={handleSkipClone}
        onClone={handleCloneUnit}
        isCloneLoading={isCloneLoading}
        isSkipLoading={isLoading}
      />
    </>
  );
};

export default InitializeAddListing;
